import { MessageOutlined } from "@ant-design/icons";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Market,
    SubtaskFacings,
    TaskAssortmentMatrix,
    TaskStatus,
} from "tm-shared/types";
import { uniqueByKey } from "tm-shared/utils";

import { BottomSheet } from "@/components/BottomSheet";
import FlexContainer from "@/components/FlexContainer";
import { CommentSelectModal } from "@/components/Modals/CommentSelectModal";
import { OutlineIconButton } from "@/components/OutlineIconButton";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch } from "@/hooks/redux";
import { useActiveScrollNavigator } from "@/hooks/useActiveScrollNavigator";
import { useStickyFilteredItems } from "@/hooks/useStickyFilteredItems";
import {
    useSubtaskConfigSelector,
    useSubtaskFacingsSelector,
} from "@/hooks/useTaskSelectors";
import { patchFacingsEntity } from "@/store/slices/progress";
import { addModalToQueue, setMdTasksConfig } from "@/store/slices/ui";
import { ModalType } from "@/types/modals";

import { useAssortmentMatrixCompletion } from "../hooks/useAssortmentMatrixCompletion";
import { StepButton } from "../StepButton";
import { StyledList } from "../Styled";
import { FacingTask } from "./FacingTask";
import { StyledBottomSheetTop, StyledFacingsMainContainer } from "./Styled";

interface FacingsProps {
    subtask: SubtaskFacings;
    market: Market;
    task: TaskAssortmentMatrix;
}

export const Facings: FC<FacingsProps> = ({ subtask, market, task }) => {
    const dispatch = useAppDispatch();

    const marketId = market.id;
    const taskId = task.id;
    const subtaskId = subtask.subtaskId;
    const contractId = subtask.contractId;

    const previousActiveFacingRef = useRef<string | null>(null);

    const [modal, setModal] = useState<"comment" | null>(null);

    // Селектори
    const facingsData = useSubtaskFacingsSelector({
        taskId,
        subtaskId,
    });

    const { activeItem: activeFacing, showUncompleted: showUncompletedFilter } =
        useSubtaskConfigSelector({
            marketId,
            taskId,
            subtaskId,
        });

    // Мемоізовані дані
    const activeFacingPayload = useMemo(
        () => facingsData?.facings?.[activeFacing] || null,
        [facingsData?.facings, activeFacing],
    );

    const currentFacings = useMemo(
        () => uniqueByKey(subtask.context.facings, "finGroup"),
        [subtask.context.facings],
    );

    // Фільтрація фейсінгів
    const { filteredItems: filteredFacings } = useStickyFilteredItems({
        items: currentFacings,
        showUncompleted: showUncompletedFilter ?? false,
        getItemId: (facing) => facing.finGroup,
        payloadsMap: facingsData?.facings ?? {},
        isIncomplete: (payload) => !payload || payload.amount === null,
    });

    // Трансформація даних для useAssortmentMatrixCompletion
    const payloadsMapForCompletion = useMemo(
        () =>
            Object.entries(facingsData?.facings ?? {}).reduce(
                (acc, [finGroup, p]) => ({
                    ...acc,
                    [finGroup]: {
                        comment: p.comment,
                        status:
                            p.amount === null
                                ? TaskStatus.Incomplete
                                : TaskStatus.Completed,
                    },
                }),
                {},
            ),
        [facingsData?.facings],
    );

    const resetCommentIfNeeded = useCallback(
        (finGroup: string) => {
            const facingPayload = facingsData?.facings?.[finGroup];

            if (!facingPayload) return;

            const hasNoAmount =
                facingPayload.amount === null ||
                facingPayload.amount === undefined;

            if (hasNoAmount && facingPayload.comment) {
                dispatch(
                    patchFacingsEntity({
                        taskId,
                        subtaskId,
                        contractId,
                        finGroup,
                        patch: { comment: "" },
                    }),
                );
            }
        },
        [facingsData?.facings, dispatch, taskId, subtaskId, contractId],
    );

    const handleCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                marketId,
                taskId,
                subtaskId,
                value: null,
            }),
        );
    }, [dispatch, marketId, taskId, subtaskId]);

    const handleOpenCommentModal = useCallback(() => {
        if (!activeFacing) return;

        setModal("comment");
    }, [
        activeFacingPayload?.comment,
        marketId,
        taskId,
        subtaskId,
        contractId,
        activeFacing,
    ]);

    const handleOpenTaskInfoModal = useCallback(() => {
        dispatch(
            addModalToQueue({
                type: ModalType.TaskInfo,
                id: ModalType.TaskInfo,
                payload: {
                    title: subtask.name || "Фейсінги",
                    description:
                        subtask.description ||
                        "Введіть кількість побачених фейсінгів",
                },
            }),
        );
    }, [dispatch, subtask.name, subtask.description]);

    const handleChangeActiveFacing = useCallback(
        (finGroup: string) => {
            dispatch(
                setMdTasksConfig({
                    marketId,
                    taskId,
                    subtaskId,
                    value: finGroup,
                }),
            );
        },
        [dispatch, marketId, taskId, subtaskId],
    );

    const onChangeFact = useCallback(
        (finGroup: string, value: number | null) => {
            dispatch(
                patchFacingsEntity({
                    taskId,
                    subtaskId,
                    contractId,
                    finGroup,
                    patch: { amount: value },
                }),
            );
        },
        [dispatch, taskId, subtaskId, contractId],
    );

    useEffect(() => {
        const previousFacing = previousActiveFacingRef.current;

        if (previousFacing && activeFacing !== previousFacing) {
            resetCommentIfNeeded(previousFacing);
        }

        previousActiveFacingRef.current = activeFacing ?? null;
    }, [activeFacing, resetCommentIfNeeded]);

    // Навігація
    const { getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredFacings.map((p) => p.finGroup),
            activeId: activeFacing,
            onChangeActive: handleChangeActiveFacing,
        });

    // Перевірка завершеності
    useAssortmentMatrixCompletion({
        marketId,
        taskId,
        subtaskId,
        expectedCount: currentFacings.length,
        payloadsMap: payloadsMapForCompletion,
    });

    const isCommentButtonDisabled = useMemo(
        () =>
            activeFacingPayload?.amount === null ||
            activeFacingPayload?.amount === undefined,
        [activeFacingPayload?.amount],
    );

    const handleChangeComment = useCallback(
        (newComment: string) => {
            dispatch(
                patchFacingsEntity({
                    taskId,
                    subtaskId,
                    finGroup: activeFacing,
                    contractId,
                    patch: { comment: newComment },
                }),
            );
        },
        [taskId, subtaskId, activeFacing, contractId],
    );

    useEffect(() => {
        scrollToActive();
    }, [facingsData?.completed]);

    return (
        <>
            <StyledFacingsMainContainer>
                <StyledList>
                    {filteredFacings.map((facingInfo) => {
                        const facingPayload =
                            facingsData?.facings?.[facingInfo.finGroup];
                        const isActive = facingInfo.finGroup === activeFacing;

                        return (
                            <div
                                key={facingInfo.finGroup}
                                ref={getItemRef(facingInfo.finGroup)}
                            >
                                <FacingTask
                                    finGroup={facingInfo.finGroup}
                                    plan={facingInfo.quantity}
                                    value={facingPayload?.amount ?? null}
                                    isActive={isActive}
                                    onChangeActive={handleChangeActiveFacing}
                                    payload={facingPayload}
                                    onChangeFact={onChangeFact}
                                />
                            </div>
                        );
                    })}
                </StyledList>

                <StepButton
                    task={task}
                    marketId={marketId}
                    currentCompleted={facingsData?.completed}
                />
            </StyledFacingsMainContainer>

            <BottomSheet
                open={!!activeFacing}
                onClose={handleCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={handleOpenTaskInfoModal}
            >
                <StyledBottomSheetTop>
                    <PrimaryText>Кі-сть фейсів {activeFacing}</PrimaryText>

                    <FlexContainer gap="20px">
                        <OutlineIconButton
                            icon={<MessageOutlined />}
                            onClick={handleOpenCommentModal}
                            disabled={isCommentButtonDisabled}
                        />
                    </FlexContainer>
                </StyledBottomSheetTop>
            </BottomSheet>

            {modal === "comment" && (
                <CommentSelectModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={handleChangeComment}
                    initialComment={activeFacingPayload?.comment ?? ""}
                />
            )}
        </>
    );
};
